import React, { useCallback, useEffect, useRef, useState } from 'react';
import "./charting/charts.css";
import "./SiteOverviewPage.css";
import Dropdown from '../../shared/forms/Dropdown';
import { generatePeriodOptions } from '../../shared/lib/dateUtils';
import { useSites } from '../../contexts/SitesContext';
import { useParams } from 'react-router-dom';
import { useDropdown } from '../../contexts/DropdownContext';
import MultiSelectDropdown from '../../shared/forms/MultiSelectDropdown';
import Content from './Content';

const SiteOverviewPage = () => {
  const { website } = useParams();
  const { sites } = useSites();
  const [site, setSite] = useState(null);
  const { getSelectedOption } = useDropdown();
  const [period, setPeriod] = useState(null);
  const [platforms, setPlatforms] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState(null);
  const [selectedCampaigns, setSelectedCampaigns] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState(null);
  const selectedPeriod = getSelectedOption('period');
  const overviewParamsRef = useRef({
    website,
    period,
    selectedPlatforms,
  });
  const sitesRef = useRef(sites);

  const fetchSiteOverviewData = useCallback((path, body, signal) => {
    return fetch(process.env.REACT_APP_SERVER_URL + path, {
      method: 'POST',
      headers: {
        'Authorization': localStorage.getItem('token'),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body),
      signal,
    }).then((response) => response.json());
  }, []);

  useEffect(() => {
    overviewParamsRef.current = {
      website,
      period,
      selectedPlatforms,
    };
  }, [period, selectedPlatforms, website]);

  useEffect(() => {
    sitesRef.current = sites;
  }, [sites]);

  useEffect(() => {
    setSite(null);
    setPlatforms(null);
    setCampaigns(null);
    setSelectedPlatforms(null);
    setSelectedCampaigns(null);
  }, [website]);

  useEffect(() => {
    if (!selectedPeriod || selectedPeriod === period) return;

    setPeriod(selectedPeriod);
    setPlatforms(null);
    setCampaigns(null);
    setSelectedPlatforms(null);
    setSelectedCampaigns(null);
  }, [period, selectedPeriod]);

  useEffect(() => {
    if (!period) return;

    const controller = new AbortController();

    setLoadingMessage('Loading platforms...');

    fetchSiteOverviewData('/api/get-platforms', {
      domain: website,
      period,
    }, controller.signal)
      .then(data => {
        if (controller.signal.aborted) return;
        if (data.success) {
          setPlatforms(data.platforms);
          setSelectedPlatforms(data.platforms);
          return;
        }

        setLoadingMessage(null);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setLoadingMessage(null);
        console.error('Failed to load platforms:', error);
      });

    return () => controller.abort();
  }, [fetchSiteOverviewData, period, website]);

  useEffect(() => {
    if (!selectedPlatforms) return;

    const controller = new AbortController();
    const { website: activeWebsite, period: activePeriod } = overviewParamsRef.current;

    setLoadingMessage('Loading campaigns...');
    setCampaigns(null);
    setSelectedCampaigns(null);

    fetchSiteOverviewData('/api/get-campaigns', {
      domain: activeWebsite,
      period: activePeriod,
      platforms: selectedPlatforms,
    }, controller.signal)
      .then(data => {
        if (controller.signal.aborted) return;
        if (data.success) {
          setSelectedCampaigns(data.campaigns);
          setCampaigns(data.campaigns);
          return;
        }

        setCampaigns(null);
        setSelectedCampaigns(null);
        setLoadingMessage(null);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setCampaigns(null);
        setSelectedCampaigns(null);
        setLoadingMessage(null);
        console.error('Failed to load campaigns:', error);
      });

    return () => controller.abort();
  }, [fetchSiteOverviewData, selectedPlatforms]);

  useEffect(() => {
    if (!selectedCampaigns) return;

    const controller = new AbortController();
    const {
      website: activeWebsite,
      period: activePeriod,
      selectedPlatforms: activePlatforms,
    } = overviewParamsRef.current;

    setLoadingMessage('Fetching site data...');

    fetchSiteOverviewData('/api/get-site-data', {
      domain: activeWebsite,
      period: activePeriod,
      platforms: activePlatforms,
      campaigns: selectedCampaigns,
    }, controller.signal)
      .then(data => {
        if (controller.signal.aborted) return;
        if (!data.success) {
          setLoadingMessage(null);
          return;
        }

        const base = sitesRef.current.find((candidate) => candidate.website === activeWebsite);
        if (!base) {
          setLoadingMessage(null);
          return;
        }

        const next = {
          ...base,
          data: data.data,
          oldCounts: data.oldCounts,
          platforms: data.platforms,
          campaigns: data.campaigns,
          lastDay: data.lastDay,
        };

        setLoadingMessage(null);
        setSite(next);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setLoadingMessage(null);
        console.error('Failed to load site overview data:', error);
      });

    return () => controller.abort();
  }, [
    selectedCampaigns,
    fetchSiteOverviewData,
  ]);

  return (
    <div className="page-shell site-overview">
      <div className="dashboard-filter-bar dashboard-filter-bar--inline">
        <Dropdown
          id='period'
          title='Period'
          options={generatePeriodOptions()}
        />
        {platforms !== null && (
          <MultiSelectDropdown
            title='Platforms'
            options={platforms}
            selectedOptions={selectedPlatforms}
            setSelectedOptions={setSelectedPlatforms}
          />
        )}
        {campaigns !== null && (
          <MultiSelectDropdown
            title='Campaigns'
            options={campaigns}
            selectedOptions={selectedCampaigns}
            setSelectedOptions={setSelectedCampaigns}
          />
        )}
      </div>
      <h2 className="page-header">Overview</h2>
      <Content site={site} loadingMessage={loadingMessage} />
    </div>
 );
};

export default SiteOverviewPage;
